import React, { useState } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { AlertTriangle, CalendarDays, CheckCircle2, Info, RefreshCw, Settings2 } from "lucide-react";

const DATES = [["actual_reserve_date", "Reserve Date"], ["projected_appointment_date", "Projected Appointment Date"],
  ["career_date", "Career / Appointment Date"], ["fta_date", "FTA Date"]];
const REQ_STATUS = [["not_started", "Not Started"], ["ready_for_review", "Pending Verification"], ["complete", "Verified Complete"],
  ["action_needed", "Needs Attention"], ["waived", "Waived"], ["not_applicable", "Not Applicable"]];
const SOURCES = [["current_verification", "Current Verification"], ["historical_record", "Historical Record"], ["imported_record", "Imported Record"]];
const d10 = (v) => (v ? String(v).slice(0, 10) : "");

function IntegrityBanner({ integrity }) {
  if (!integrity) return null;
  const issues = [...(integrity.issues || []), ...(integrity.progress_issues || [])];
  if (!issues.length) return null;
  const serious = issues.some((i) => i.severity !== "info");
  return (
    <div className={`rounded-lg border p-3 text-sm ${serious ? "border-amber-300 bg-amber-50 text-amber-900" : "border-slate-200 bg-slate-50 text-slate-700"}`} data-testid="mpp-integrity">
      <p className="font-semibold flex items-center gap-1.5">{serious ? <AlertTriangle className="w-4 h-4" /> : <Info className="w-4 h-4" />}{serious ? "Review required" : "Configuration notes"}</p>
      <p className="text-xs mt-1">This version's phases: {(integrity.version_phases || []).join(" → ")}</p>
      {issues.map((i) => (
        <div key={i.kind} className="mt-2 text-xs" data-testid={`mpp-issue-${i.kind}`}>
          <p>{i.message}</p>
          {i.proposed_matches?.length > 0 && <ul className="mt-1 ml-4 list-disc">{i.proposed_matches.map((m) => (
            <li key={m.source_title}>{m.source_title} ({m.source_status}) → {m.target_title || "no confident match"} · <strong>{m.result}</strong></li>))}</ul>}
        </div>
      ))}
    </div>
  );
}

function DatesForm({ enr, onSaved }) {
  const [f, setF] = useState(Object.fromEntries(DATES.map(([k]) => [k, (enr[k] || "").slice(0, 10)])));
  const save = async () => {
    try { await api.patch(`/programs/enrollments/${enr.id}/dates`, f); toast.success("Program dates saved"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <div data-testid="mpp-dates">
      <p className="text-xs font-semibold uppercase text-slate-500 mb-2 flex items-center gap-1.5"><CalendarDays className="w-3.5 h-3.5" />Program Dates</p>
      <div className="grid sm:grid-cols-4 gap-3">{DATES.map(([k, l]) => (
        <div key={k}><Label className="text-xs">{l}</Label><Input type="date" value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} data-testid={`mpp-date-${k}`} /></div>))}</div>
      <Button size="sm" variant="outline" className="mt-2" onClick={save} data-testid="mpp-dates-save">Save Dates</Button>
      <p className="text-[11px] text-slate-400 mt-1">Projected Appointment is a projection — it never marks a milestone or appointment complete.</p>
    </div>
  );
}

function History({ req }) {
  const h = req.override_history || [];
  return (
    <div className="rounded-md bg-slate-50 border border-slate-200 p-2 text-[11px] text-slate-600 space-y-1" data-testid="mpp-req-history">
      <p><b>Source:</b> {req.source_ref ? `${req.source_ref.label}${req.source_ref.date_rule ? ` · date rule: ${req.source_ref.date_rule}` : ""}` : "No linked supporting record (entered on this screen or historical)"}</p>
      {req.submitted_at && <p><b>Submitted:</b> {d10(req.submitted_at)}</p>}
      {h.length === 0 ? <p>No manual changes recorded.</p> : h.slice(-5).reverse().map((x, i) => (
        <p key={i}>{d10(x.at)} · {x.by_name}: {x.field} {JSON.stringify(x.previous)} → {JSON.stringify(x.new)} — “{x.reason}”</p>))}
    </div>
  );
}

function VerifyDialog({ enrollmentId, req, onClose, onSaved }) {
  const [f, setF] = useState({ status: req.status, completed_on: d10(req.completed_at), verified_at: "", verification_source: "current_verification", reason: "" });
  const save = async () => {
    const payload = { enrollment_id: enrollmentId, requirement_id: req.requirement_id, reason: f.reason };
    if (f.status !== req.status) payload.status = f.status;
    if (f.status === "complete") {
      if (f.completed_on && f.completed_on !== d10(req.completed_at)) payload.completed_on = f.completed_on;
      if (payload.status) { payload.verification_source = f.verification_source; if (f.verified_at) payload.verified_at = f.verified_at; }
    }
    if (!payload.status && !payload.completed_on) { onClose(); return; }
    try { await api.patch(`/programs/milestone-status/${req.status_id || "_"}`, payload); toast.success("Requirement updated"); onSaved(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const statuses = REQ_STATUS.filter(([k]) => !(req.non_waivable && (k === "waived" || k === "not_applicable")));
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white" data-testid="mpp-verify-dialog" aria-describedby={undefined}>
        <DialogHeader><DialogTitle>{req.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <History req={req} />
          <div><Label className="text-xs">Status</Label>
            <Select value={f.status} onValueChange={(v) => setF({ ...f, status: v })}>
              <SelectTrigger data-testid="mpp-verify-status"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{statuses.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select>
            {req.non_waivable && <p className="text-[11px] text-slate-500 mt-1">This requirement can't be waived — it must be completed and verified.</p>}</div>
          {f.status === "complete" && <div className="grid grid-cols-2 gap-2">
            <div><Label className="text-xs">Completed On (event / effective date)</Label>
              <Input type="date" value={f.completed_on} onChange={(e) => setF({ ...f, completed_on: e.target.value })} data-testid="mpp-completed-on" /></div>
            {f.status !== req.status && <div><Label className="text-xs">Verified On (blank = now)</Label>
              <Input type="date" value={f.verified_at} onChange={(e) => setF({ ...f, verified_at: e.target.value })} data-testid="mpp-verify-date" /></div>}
            {f.status !== req.status && <div className="col-span-2"><Select value={f.verification_source} onValueChange={(v) => setF({ ...f, verification_source: v })}>
              <SelectTrigger data-testid="mpp-verify-source"><SelectValue /></SelectTrigger>
              <SelectContent className="bg-white">{SOURCES.map(([k, l]) => <SelectItem key={k} value={k}>{l}</SelectItem>)}</SelectContent>
            </Select></div>}
          </div>}
          {req.status !== "complete" && f.status !== "complete" && <p className="text-[11px] text-slate-500">To set a completion date, choose Verified Complete — a date correction alone never verifies a requirement.</p>}
          <div><Label className="text-xs">Reason for this change (required)</Label>
            <Textarea rows={2} value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} data-testid="mpp-reason" /></div>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={!f.reason.trim()} data-testid="mpp-verify-save" className="bg-[#006090] hover:bg-[#004E75]">Save</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ConflictRow({ req, onChanged }) {
  const c = req.source_conflict;
  const resolve = async (keep) => {
    const reason = window.prompt(keep === "source" ? "Reason for returning to the source value:" : "Reason for keeping the manual value:");
    if (!reason) return;
    try { await api.post(`/programs/milestone-status/${req.status_id}/resolve-conflict`, { keep, reason }); toast.success("Resolved"); onChanged(); }
    catch (e) { toast.error(apiError(e)); }
  };
  return (
    <tr className="bg-amber-50" data-testid={`mpp-conflict-${req.requirement_id}`}><td colSpan={7} className="p-2 text-[11px] text-amber-900">
      <AlertTriangle className="w-3.5 h-3.5 inline mr-1" />Source record ({c.label}) {c.kind === "date" ? `says completed on ${c.source_date}, not ${c.current_date}` : c.kind === "unverified_source" ? "is not verified yet, but this requirement is marked complete — verify the license record or keep the manual value" : `says ${c.source_status}${c.source_date ? ` (${c.source_date})` : ""}`}.
      <Button size="sm" variant="outline" className="h-6 text-[11px] ml-2" onClick={() => resolve("override")} data-testid={`mpp-keep-override-${req.requirement_id}`}>Keep manual value</Button>
      <Button size="sm" variant="outline" className="h-6 text-[11px] ml-1" onClick={() => resolve("source")} data-testid={`mpp-use-source-${req.requirement_id}`}>Use source value</Button>
    </td></tr>
  );
}

function RequirementsTable({ data, onEdit, onChanged }) {
  const canEdit = data.can_edit_requirements;
  return (
    <div data-testid="mpp-requirements">
      <p className="text-xs font-semibold uppercase text-slate-500 mb-2">Official Program Requirements · {data.required_complete} of {data.required_total} verified</p>
      <div className="max-h-96 overflow-y-auto rounded-lg border border-slate-200">
        <table className="w-full text-xs"><thead className="bg-slate-50 text-slate-500 sticky top-0"><tr><th className="text-left p-2">Requirement</th><th className="text-left p-2">Phase</th><th className="text-left p-2">Status</th><th className="text-left p-2">Completed On</th><th className="text-left p-2">Verified On / By</th><th className="text-left p-2">Source</th><th /></tr></thead>
          <tbody>{(data.requirements || []).map((r) => (
            <React.Fragment key={r.requirement_id}>
              <tr className="border-t border-slate-100" data-testid={`mpp-req-${r.requirement_id}`}>
                <td className="p-2 font-medium text-slate-800">{r.title}{!r.required && <span className="ml-1 text-[10px] text-slate-400">(optional)</span>}</td>
                <td className="p-2 text-slate-500">{r.phase_name || <span className="text-rose-600 font-semibold">Unmapped</span>}</td>
                <td className="p-2" data-testid={`mpp-req-status-${r.requirement_id}`}>{r.status_label}{r.manual_override && r.status !== "not_started" && <span className="ml-1 text-[10px] text-slate-400">· manual</span>}</td>
                <td className="p-2 text-slate-600" data-testid={`mpp-req-completed-${r.requirement_id}`}>{d10(r.completed_at) || (r.missing_date ? <span className="text-amber-700">Date missing</span> : "—")}</td>
                <td className="p-2 text-slate-500">{r.verified_at ? `${d10(r.verified_at)} · ${r.verified_by || ""}` : "—"}</td>
                <td className="p-2 text-slate-500 max-w-[180px] truncate" title={r.source_ref?.label}>{r.source_ref?.label || (r.verification_source || "").replace(/_/g, " ") || "—"}</td>
                <td className="p-2">{canEdit && <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => onEdit(r)} data-testid={`mpp-req-edit-${r.requirement_id}`}>Update</Button>}</td>
              </tr>
              {r.source_conflict && canEdit && <ConflictRow req={r} onChanged={onChanged} />}
            </React.Fragment>))}</tbody></table>
      </div>
    </div>
  );
}

function SyncTraining({ enrollmentId, onApplied }) {
  const [p, setP] = useState(null);
  const [busy, setBusy] = useState(false);
  const preview = async () => { setBusy(true); try { setP((await api.get(`/program-tasks/enrollments/${enrollmentId}/sync-preview`)).data); } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); } };
  const apply = async () => {
    setBusy(true);
    try { const r = await api.post(`/program-tasks/enrollments/${enrollmentId}/sync-apply`); toast.success(`Synced: ${r.data.active_created} active, ${r.data.upcoming_created} upcoming`); setP(null); onApplied(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <div data-testid="mpp-sync">
      <div className="flex items-center justify-between"><div><p className="text-xs font-semibold uppercase text-slate-500">Training Project Tasks</p>
        <p className="text-[11px] text-slate-400">Phase tasks activate automatically as requirements are verified. Use this only to import new catalog tasks.</p></div>
        <Button size="sm" variant="outline" onClick={preview} disabled={busy} data-testid="mpp-sync-preview"><RefreshCw className="w-3.5 h-3.5 mr-1" />Check Catalog Tasks</Button></div>
      {p && (
        <div className="mt-2 rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs space-y-1" data-testid="mpp-sync-result">
          <p>Existing tasks: <b>{p.existing_tasks}</b> · Already satisfied: <b>{p.already_satisfied}</b></p>
          <p>New Active: <b>{p.counts.active}</b> · New Upcoming: <b>{p.counts.upcoming}</b> · Conditional: <b>{p.counts.conditional}</b> · Post-Career: <b>{p.counts.post_career}</b></p>
          <p>Duplicates / review: <b>{p.needs_review.length}</b> · Conflicts (not created): <b>{p.conflicts.length}</b></p>
          <details><summary className="cursor-pointer text-[#006090]">View tasks to add</summary><ul className="ml-4 list-disc">{p.new.map((t) => <li key={t.key}>{t.title} · {t.owner} · {t.phase} · {t.result}</li>)}</ul></details>
          <Button size="sm" onClick={apply} disabled={busy || !p.can_apply || !p.new.length} data-testid="mpp-sync-apply" className="mt-1 bg-[#006090] hover:bg-[#004E75]">Add Catalog Tasks</Button>
        </div>
      )}
    </div>
  );
}

export function ManageProgramProgress({ data, onChanged }) {
  const [editing, setEditing] = useState(null);
  const enr = data.enrollment;
  const current = (data.phases || []).find((p) => p.is_current);
  return (
    <section className="rounded-xl border border-[#006090]/20 bg-white p-4 space-y-5" data-testid="manage-program-progress">
      <h3 className="font-display font-semibold text-slate-900 flex items-center gap-2"><Settings2 className="w-4 h-4 text-[#006090]" />Manage Program Progress</h3>
      <p className="text-xs text-slate-500">{({ associate: "Associate Agent", protege: "Protégé", retail_traditional: "Retail Traditional", retail: "Retail Acquisition", agency_producer: "Agency Producer" })[enr.program] || enr.program} · Version {enr.version_label} · Current Phase: <b data-testid="mpp-current-phase">{current?.name || "Not determined"}</b> · Status: <b className="capitalize">{enr.status}</b>
        {data.integrity?.ok && <span className="ml-2 text-emerald-700 inline-flex items-center gap-1" data-testid="mpp-config-ok"><CheckCircle2 className="w-3 h-3" />Configuration valid · phase calculated from verified requirements</span>}</p>
      {data.waiting_on && <p className="text-xs rounded-md bg-sky-50 border border-sky-200 text-sky-900 px-3 py-2" data-testid="mpp-waiting-on"><b>{data.waiting_on.message}</b>{data.waiting_on.requirement_title ? `: ${data.waiting_on.requirement_title}` : ""} · {data.waiting_on.owner_label}</p>}
      <IntegrityBanner integrity={data.integrity} />
      <DatesForm key={enr.id + (enr.updated_at || "")} enr={enr} onSaved={onChanged} />
      <RequirementsTable data={data} onEdit={setEditing} onChanged={onChanged} />
      <SyncTraining enrollmentId={enr.id} onApplied={onChanged} />
      {editing && <VerifyDialog key={editing.requirement_id} req={editing} enrollmentId={enr.id} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); onChanged(); }} />}
    </section>
  );
}
